import { defineStore } from "pinia";
import { isEmpty, isNil } from "lodash";
import axios from "axios"; // Assuming you use this based on your actions

const createStoreOptions = (storageKey, isOffer) => ({
    state: () => ({
        activeStep: 1,
        vehicleData: null,
        licensePlate: '',
        garages: [],
        activeGarage: null,
        date: null,
        time: '',
        services: [],
        selectedServiceIds: [],
        otherWork: '',
        csrfToken: '',
        formError: false,
        redirectUrl: '',
        blockedDates: [],
        initialGarage: {},
        isRequestOffer: isOffer, // <--- Set dynamically!

        formData: {
            first_name: '',
            last_name: '',
            email_address: '',
            phone_number: '',
            zip_code: '',
            house_number: '',
            house_number_addition: '',
            street: '',
            city: '',
            mileage: '',
            make_and_model: '',
            comments: '',
        }
    }),

    getters: {
        hasLicensePlate: (state) => !isEmpty(state.licensePlate),
        hasVehicleData: (state) => !isNil(state.vehicleData),
        hasGarages: (state) => !isEmpty(state.garages),
        hasActiveGarage: (state) => !isNil(state.activeGarage),
        hasOtherWork: (state) => !isEmpty(state.otherWork),
        hasInitialGarage: (state) => !isEmpty(state.initialGarage),
        activeServices: (state) => {
            return state.services
                .filter(service => state.selectedServiceIds.includes(service.id))
                .map(({ id, name }) => ({ id, name }));
        }
    },

    actions: {
        goToStep(step) {
            window.scrollTo(0, 0);
            this.activeStep = step;
        },

        goFurtherWithoutLicensePlate() {
            this.licensePlate = '';
            this.vehicleData = null;
            this.goToStep(2);
        },

        setActiveGarage(garage) {
            this.activeGarage = garage;
        },

        handleReset() {
            this.vehicleData = null;
            this.licensePlate = '';
            this.garages = [];
            this.goToStep(1);
        },

        resetGarage() {
            this.setActiveGarage(null);
            this.date = null;
            this.time = '';
            this.goToStep(2);
        },

        resetServices() {
            this.selectedServiceIds = [];
            this.otherWork = '';
            this.goToStep(3);
        },

        toggleServiceId(id) {
            const index = this.selectedServiceIds.indexOf(id);

            if (index === -1) {
                this.selectedServiceIds.push(id);
                return;
            }

            this.selectedServiceIds.splice(index, 1);
        },

        resetForm() {
            const redirectUrl = this.redirectUrl;
            this.$reset();
            window.location = redirectUrl;
        },

        setSessionToken() {
            axios.get('/session/token')
                .then(response => {
                    this.csrfToken = response.data;
                })
                .catch(error => {
                    console.error(error);
                    this.csrfToken = '';
                });
        },

        submitAppointment() {
            this.formError = false;

            const garageId = this.activeGarage?.id;

            const formData = {
                ...this.formData,
                webform_id: this.isRequestOffer ? 'request_a_appointment_form' : 'appointment_form',
                license_plate: this.licensePlate ?? '',
                services: this.selectedServiceIds,
                other_work: this.otherWork,
                garage: garageId,
                appointment_date: this.date,
                timeslot: this.time,
                entity_type: 'garage_entity',
                entity_id: garageId,
            };

            axios.post('/webform_rest/submit', formData, {
                headers: { 'X-CSRF-Token': this.csrfToken }
            })
                .then(response => {
                    this.pushSuccessToDataLayer();
                    this.resetForm();
                })
                .catch(error => {
                    this.formError = true;
                });
        },

        pushSuccessToDataLayer() {
            const event = this.isRequestOffer ? "offerte_aanvraag" : "afspraak_aanvraag";
            const vehicle = this.vehicleData ? `${this.vehicleData.make} ${this.vehicleData.model} ${this.vehicleData.year}` : "Onbekend";
            const servicesString = this.activeServices.map(s => s.name).join(', ');
            const servicesCount = this.activeServices.length;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: event,
                stap: "5. Bedankt voor uw aanvraag",
                voertuig: vehicle,
                vestiging: this.activeGarage?.name ?? '',
                afspraak: this.date ?? '',
                afspraak_moment: this.time ?? '',
                services: servicesString,
                services_aantal: servicesCount
            });
        }
    },

    persist: {
        enabled: true,
        strategies: [
            {
                key: storageKey,
                storage: sessionStorage
            }
        ]
    }
});

export const useAppointmentStore = defineStore('appointment', createStoreOptions('appointment_store', false));

export const useOfferStore = defineStore('offer', createStoreOptions('appointment_offer_store', true));